import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling"); // polling, success, failed
  const sessionId = searchParams.get("session_id");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    if (attempts.current >= 8) {
      setStatus("failed");
      return;
    }
    attempts.current += 1;

    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === "paid") {
        setStatus("success");
        return;
      }
      if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }
      // Keep polling
      setTimeout(pollStatus, 2500);
    } catch (err) {
      setTimeout(pollStatus, 2500);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="subscription-success-page">
        {status === "polling" && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
            <h2 className="text-2xl font-serif font-bold">Verifying Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-serif font-bold">Subscription Activated!</h2>
            <p className="text-muted-foreground">Your plan has been upgraded. All features are now unlocked.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-4" data-testid="go-to-dashboard">
              Return to Dashboard
            </Button>
          </div>
        )}

        {status === "failed" && (
          <div className="space-y-4">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-2xl font-serif font-bold">Payment Issue</h2>
            <p className="text-muted-foreground">We couldn't verify your payment. If you were charged, your subscription will activate shortly.</p>
            <div className="flex gap-3 justify-center mt-4">
              <Button variant="outline" onClick={() => navigate("/subscription")} data-testid="retry-subscription">
                Try Again
              </Button>
              <Button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-failed">
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
